// Lightweight asset picker: upload + select. Backed by the project's
// `Page_Design/assets/` folder via `/api/designers/assets/*`. Falls back
// gracefully to free-text URL if the asset endpoint is unavailable, so
// pages with raw URLs continue to work.

import { useEffect, useRef, useState } from 'react'
import { Image as ImageIcon, Upload, X, Sparkles, Check } from 'lucide-react'

import api from '../../../api/client'

export interface ProjectAsset {
  id: string
  name: string
  url: string
  mime?: string
  size?: number
  width?: number
  height?: number
  createdAt?: string
}

interface ListResponse {
  success: boolean
  assets: ProjectAsset[]
}

async function listAssets(project: string): Promise<ProjectAsset[]> {
  try {
    const r = await api.get<ListResponse>('/designers/assets/list', { params: { project } })
    return r.data?.assets ?? []
  } catch {
    return []
  }
}

async function uploadAsset(project: string, file: File): Promise<ProjectAsset | null> {
  try {
    const fd = new FormData()
    fd.append('project', project)
    fd.append('file', file)
    const r = await api.post<{ success: boolean; asset: ProjectAsset }>(
      '/designers/assets/upload',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return r.data?.asset ?? null
  } catch {
    return null
  }
}

// One eligible image-generation configuration (a named LLM config the user
// already defined under Settings → LLM, on an image-capable provider).
export interface ImageConfigOption {
  name: string
  provider: string
  model: string
}
interface ImageOptions {
  configs: ImageConfigOption[]
  selected: string | null            // the configured default config name
  legacy: { provider: string; model: string } | null
}
// Which config a generation actually used, echoed back by the server.
interface UsedConfig {
  config_name?: string | null
  provider?: string | null
  model?: string | null
}

// Fetch the LLM configs eligible for image generation + the current default
// selection, so the modal can show and let the user switch which config runs.
async function getImageOptions(): Promise<ImageOptions> {
  try {
    const r = await api.get<{ success: boolean } & ImageOptions>('/designers/assets/image-options')
    return {
      configs: r.data?.configs ?? [],
      selected: r.data?.selected ?? null,
      legacy: r.data?.legacy ?? null,
    }
  } catch {
    return { configs: [], selected: null, legacy: null }
  }
}

// Generate an image from a prompt. `configName` optionally overrides which
// LLM config is used for this one generation (the modal's inline selector);
// when omitted the server uses the configured image-generation default. The
// backend saves the image to the asset library and returns the asset record
// plus the config it actually used. Throws with a readable message on
// failure (e.g. profile not configured) so the modal can surface it.
async function generateAsset(
  project: string, prompt: string, configName?: string,
): Promise<{ asset: ProjectAsset; source?: string; used?: UsedConfig | null }> {
  const r = await api.post<{ success: boolean; asset: ProjectAsset; source?: string; used?: UsedConfig | null }>(
    '/designers/assets/generate',
    { project, prompt, config_name: configName || undefined },
    // Image generation can take a few minutes; give the request a generous
    // ceiling so it doesn't hang forever if the connection is dropped. The
    // asset-list poller (see handleGenerate) is the resilient fallback.
    { timeout: 300_000 },
  )
  if (!r.data?.asset) throw new Error('No image returned')
  return { asset: r.data.asset, source: r.data.source, used: r.data.used }
}

interface Props {
  project: string
  value?: string                          // current URL
  onChange: (url: string) => void
  accept?: string                         // 'image/*'
  label?: string
}

export function AssetPickerField({ project, value, onChange, accept = 'image/*', label }: Props) {
  const [open, setOpen] = useState(false)
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {label && <label style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{label}</label>}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          type="text"
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder="https://… or pick from library"
          style={{
            flex: 1, padding: '6px 8px', fontSize: 14,
            border: '1px solid var(--color-border)', borderRadius: 4,
            background: 'var(--color-bg)', color: 'var(--color-text)',
          }}
        />
        <button
          type="button"
          onClick={() => setOpen(true)}
          title="Open asset library"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            padding: '6px 8px', fontSize: 13,
            border: '1px solid var(--color-border)', borderRadius: 4,
            background: 'var(--color-bg-secondary)', cursor: 'pointer',
          }}
        >
          <ImageIcon size={14} /> Library
        </button>
      </div>
      {value && /\.(png|jpe?g|gif|webp|svg)$/i.test(value) && (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img src={value} style={{ maxWidth: '100%', maxHeight: 80, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--color-border)' }} />
      )}
      {open && (
        <AssetPickerModal
          project={project}
          accept={accept}
          onClose={() => setOpen(false)}
          onSelect={url => { onChange(url); setOpen(false) }}
        />
      )}
    </div>
  )
}

interface ModalProps {
  project: string
  accept: string
  onClose: () => void
  onSelect: (url: string) => void
  /** Stacking order — a host that opens it over its own layers (website
   *  Make changes) passes one above them. */
  zIndex?: number
}
export function AssetPickerModal({ project, accept, onClose, onSelect, zIndex = 100 }: ModalProps) {
  const [items, setItems] = useState<ProjectAsset[]>([])
  const [busy, setBusy]   = useState(false)
  const [tab, setTab]     = useState<'library' | 'generate'>('library')
  const [prompt, setPrompt] = useState('')
  const [genBusy, setGenBusy] = useState(false)
  const [genError, setGenError] = useState<string | null>(null)
  // Image-gen configs + the user's inline choice for this session. `chosen`
  // is the config_name to use; '' means "use the configured default".
  const [imgOptions, setImgOptions] = useState<ImageOptions | null>(null)
  const [chosen, setChosen] = useState<string>('')
  // A freshly generated image awaiting the user's "Use this image" — we no
  // longer auto-apply so the user can preview and regenerate first.
  const [preview, setPreview] = useState<{ asset: ProjectAsset; used?: UsedConfig | null } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  // Set false on unmount so the async generation poller stops touching state.
  const mountedRef = useRef(true)
  useEffect(() => () => { mountedRef.current = false }, [])

  useEffect(() => { listAssets(project).then(setItems) }, [project])

  // Load image-gen options once (configs are user/tenant-scoped, not per
  // project). Default the inline choice to the configured selection.
  useEffect(() => {
    let cancelled = false
    getImageOptions().then((opts) => {
      if (cancelled) return
      setImgOptions(opts)
      setChosen(opts.selected || '')
    })
    return () => { cancelled = true }
  }, [])

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    setBusy(true)
    const uploaded: ProjectAsset[] = []
    for (const f of Array.from(files)) {
      const a = await uploadAsset(project, f)
      if (a) uploaded.push(a)
    }
    setItems(prev => [...uploaded, ...prev])
    setBusy(false)
    // Auto-select the freshly uploaded image (closes the modal). Uploading
    // an image is an explicit "use this" action, so picking it is the
    // expected next step.
    if (uploaded.length) onSelect(uploaded[0].url)
  }

  async function handleGenerate() {
    const p = prompt.trim()
    if (!p || genBusy) return
    setGenBusy(true)
    setGenError(null)
    setPreview(null)

    // Image generation is slow (often minutes), and a single long-held POST
    // can be dropped by dev proxies / load balancers before the response
    // reaches the browser — leaving the UI stuck while the backend actually
    // succeeded. The backend saves the image to the library *before* it
    // responds, so we run two paths and take whichever lands first:
    //   1. the POST response (carries the exact config used), and
    //   2. a poller on the asset list that detects the new `ai-*` asset.
    const beforeIds = new Set(items.map(a => a.id))
    let done = false
    const finish = (asset: ProjectAsset, used: UsedConfig | null) => {
      if (done || !mountedRef.current) return
      done = true
      setItems(prev => (prev.some(a => a.id === asset.id) ? prev : [asset, ...prev]))
      setPreview({ asset, used })
      setGenBusy(false)
    }

    // Primary path — the POST. Fast misconfiguration errors (HTTP 4xx) carry
    // a response and should surface immediately; a bare network/timeout error
    // (no response) means the backend may still be working, so we let the
    // poller keep going instead of failing.
    generateAsset(project, p, chosen)
      .then(({ asset, used }) => finish(asset, used ?? resolveUsedForDisplay()))
      .catch((e: any) => {
        if (done || !mountedRef.current) return
        if (e?.response) {
          done = true
          setGenBusy(false)
          setGenError(
            e.response.data?.detail ||
            e.message ||
            'Image generation failed. Check your image-generation configuration in Settings → LLM.',
          )
        }
        // else: connection dropped — leave it to the poller.
      })

    // Fallback path — poll the library for the freshly saved image.
    const startedAt = Date.now()
    while (!done && mountedRef.current && Date.now() - startedAt < 300_000) {
      await new Promise(r => setTimeout(r, 3000))
      if (done || !mountedRef.current) break
      const list = await listAssets(project)
      const fresh = list.find(a => !beforeIds.has(a.id) && a.name.startsWith('ai-'))
      if (fresh) { finish(fresh, resolveUsedForDisplay()); break }
    }
    if (!done && mountedRef.current) {
      done = true
      setGenBusy(false)
      setGenError('Generation is taking longer than expected. Check the Library tab shortly — the image may still appear there.')
    }
  }

  // AI generation produces images, so only offer it on image pickers.
  const canGenerate = accept.includes('image')

  // A human-readable label for a config the modal knows about.
  const labelForConfig = (c: ImageConfigOption) =>
    `${c.name} — ${c.provider === 'openai' ? 'OpenAI' : c.provider === 'google' ? 'Google' : c.provider} · ${c.model}`
  // What "use the default" resolves to, for the disabled hint.
  const defaultLabel = (() => {
    if (!imgOptions) return ''
    const sel = imgOptions.configs.find(c => c.name === imgOptions.selected)
    if (sel) return labelForConfig(sel)
    if (imgOptions.legacy) return `${imgOptions.legacy.provider} · ${imgOptions.legacy.model || 'default'}`
    return ''
  })()
  // Generation is possible if a config is chosen, or a default/legacy exists.
  const hasUsableConfig = !!chosen || !!imgOptions?.selected || !!imgOptions?.legacy
  const usedLabel = (u?: UsedConfig | null) =>
    !u ? '' : (u.config_name
      ? `${u.config_name} (${u.provider} · ${u.model})`
      : `${u.provider} · ${u.model}`)
  // Best-effort "used config" for the polled fallback path, where the server
  // response (and its exact `used` echo) never reached us. Derived from the
  // user's current selection / default so the preview chip still shows.
  const resolveUsedForDisplay = (): UsedConfig | null => {
    const c = chosen ? imgOptions?.configs.find(x => x.name === chosen) : undefined
    if (c) return { config_name: c.name, provider: c.provider, model: c.model }
    const sel = imgOptions?.selected ? imgOptions.configs.find(x => x.name === imgOptions.selected) : undefined
    if (sel) return { config_name: sel.name, provider: sel.provider, model: sel.model }
    if (imgOptions?.legacy) return { provider: imgOptions.legacy.provider, model: imgOptions.legacy.model }
    return null
  }

  const tabBtn = (key: 'library' | 'generate', label: string): React.CSSProperties => ({
    padding: '6px 12px', fontSize: 14, cursor: 'pointer',
    border: 'none', background: 'none',
    borderBottom: tab === key ? '2px solid var(--color-accent)' : '2px solid transparent',
    color: tab === key ? 'var(--color-text)' : 'var(--color-text-secondary)',
    fontWeight: tab === key ? 600 : 400,
  })

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 720, maxHeight: '80vh', display: 'flex', flexDirection: 'column',
          background: 'var(--color-bg)', borderRadius: 8, border: '1px solid var(--color-border)',
        }}
      >
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--color-border)' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" style={tabBtn('library', 'Library')} onClick={() => setTab('library')}>Library</button>
            {canGenerate && (
              <button type="button" style={tabBtn('generate', 'Generate')} onClick={() => setTab('generate')}>Generate with AI</button>
            )}
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>
            <X size={16} />
          </button>
        </header>

        {tab === 'library' ? (
          <>
            <div style={{ padding: 16, display: 'flex', gap: 8, alignItems: 'center', borderBottom: '1px solid var(--color-border)' }}>
              <input ref={fileRef} type="file" accept={accept} multiple style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
              <button
                type="button"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', fontSize: 14,
                  border: '1px solid var(--color-accent)', borderRadius: 4,
                  background: 'var(--color-accent)', color: '#fff', cursor: 'pointer',
                }}
              >
                <Upload size={14} /> {busy ? 'Uploading…' : 'Upload'}
              </button>
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{items.length} asset{items.length === 1 ? '' : 's'}</span>
            </div>

            <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
              {items.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: 40, fontSize: 14 }}>
                  No assets yet — upload an image above or generate one with AI.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
                  {items.map(a => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => onSelect(a.url)}
                      title={a.name}
                      style={{
                        background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
                        borderRadius: 6, padding: 6, cursor: 'pointer', textAlign: 'left',
                      }}
                    >
                      {/* eslint-disable-next-line jsx-a11y/alt-text */}
                      <img src={a.url} style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 4 }} />
                      <div style={{ marginTop: 6, fontSize: 13, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {a.name}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>
            <label style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
              Describe the image you want. It’s generated with the configuration you pick below and saved to the library.
            </label>
            <textarea
              value={prompt}
              onChange={e => setPrompt(e.target.value)}
              placeholder="e.g. A minimal flat-style illustration of a rocket launching, soft gradient background"
              rows={4}
              disabled={genBusy}
              style={{
                padding: '8px 10px', fontSize: 14, resize: 'vertical',
                border: '1px solid var(--color-border)', borderRadius: 4,
                background: 'var(--color-bg)', color: 'var(--color-text)',
              }}
            />

            {/* Config selector — shows which LLM config will generate, and
                lets the user switch it for this generation (like picking a
                model when you ask AI). */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                Model configuration
              </label>
              {imgOptions && imgOptions.configs.length > 0 ? (
                <select
                  value={chosen}
                  disabled={genBusy}
                  onChange={e => setChosen(e.target.value)}
                  style={{
                    padding: '7px 10px', fontSize: 14, borderRadius: 4,
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)', color: 'var(--color-text)',
                  }}
                >
                  {/* "Default" only makes sense when a default/legacy exists. */}
                  {(imgOptions.selected || imgOptions.legacy) && (
                    <option value="">Default{defaultLabel ? ` — ${defaultLabel}` : ''}</option>
                  )}
                  {imgOptions.configs.map(c => (
                    <option key={c.name} value={c.name}>{labelForConfig(c)}</option>
                  ))}
                </select>
              ) : (
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  No image-capable LLM configuration found. Add an OpenAI or Google API
                  configuration under <strong>Settings → LLM</strong> and select it for image generation.
                </div>
              )}
            </div>

            {genError && (
              <div style={{ fontSize: 13, color: 'var(--color-danger, #dc2626)', lineHeight: 1.4 }}>{genError}</div>
            )}

            <div>
              <button
                type="button"
                disabled={genBusy || !prompt.trim() || !hasUsableConfig}
                onClick={handleGenerate}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', fontSize: 14, fontWeight: 600,
                  border: '1px solid var(--color-accent)', borderRadius: 4,
                  background: 'var(--color-accent)', color: '#fff',
                  cursor: (genBusy || !prompt.trim() || !hasUsableConfig) ? 'not-allowed' : 'pointer',
                  opacity: (genBusy || !prompt.trim() || !hasUsableConfig) ? 0.6 : 1,
                }}
              >
                <Sparkles size={14} /> {genBusy ? 'Generating…' : preview ? 'Regenerate' : 'Generate'}
              </button>
              {genBusy && (
                <span style={{ marginLeft: 10, fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  Generating with {chosen
                    ? labelForConfig(imgOptions!.configs.find(c => c.name === chosen)!)
                    : (defaultLabel || 'your configured profile')}…
                </span>
              )}
            </div>

            {/* Preview of the freshly generated image — accept or regenerate. */}
            {preview && !genBusy && (
              <div style={{
                marginTop: 4, padding: 12, borderRadius: 6,
                border: '1px solid var(--color-border)', background: 'var(--color-bg-secondary)',
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                {preview.used && (
                  <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                    Generated with <strong>{usedLabel(preview.used)}</strong>
                  </div>
                )}
                {/* eslint-disable-next-line jsx-a11y/alt-text */}
                <img
                  src={preview.asset.url}
                  style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 4, alignSelf: 'center' }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => { onSelect(preview.asset.url) }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', fontSize: 14, fontWeight: 600,
                      border: '1px solid var(--color-accent)', borderRadius: 4,
                      background: 'var(--color-accent)', color: '#fff', cursor: 'pointer',
                    }}
                  >
                    <Check size={14} /> Use this image
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreview(null)}
                    style={{
                      padding: '8px 14px', fontSize: 14,
                      border: '1px solid var(--color-border)', borderRadius: 4,
                      background: 'var(--color-bg)', color: 'var(--color-text)', cursor: 'pointer',
                    }}
                  >
                    Discard
                  </button>
                </div>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  Saved to the library either way — change the prompt or model above and Regenerate to try again.
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
